import { useCallback, useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import {
  awardPoints,
  fetchPoints,
  levelFor,
  POINT_RULES,
  type LevelDef,
} from "@/lib/gamification";
import { trackEvent } from "@/lib/audit";

export function useFanPoints() {
  const [points, setPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const lastLevel = useRef<LevelDef | null>(null);

  const refresh = useCallback(async () => {
    const p = await fetchPoints();
    setPoints(p);
    setLoading(false);
    return p;
  }, []);

  useEffect(() => {
    void refresh();
    // daily login bonus
    void awardPoints("app_open_daily").then((a) => {
      if (a > 0) void refresh();
    });
  }, [refresh]);

  // detect level-up
  useEffect(() => {
    if (loading) return;
    const { current } = levelFor(points);
    if (lastLevel.current && lastLevel.current.key !== current.key) {
      // celebrate
      const colors = ["#E10600", "#FFD700", "#10b981", "#FFFFFF"];
      const end = Date.now() + 1500;
      const tick = () => {
        confetti({ particleCount: 8, angle: 60, spread: 75, origin: { x: 0, y: 0.7 }, colors });
        confetti({ particleCount: 8, angle: 120, spread: 75, origin: { x: 1, y: 0.7 }, colors });
        if (Date.now() < end) requestAnimationFrame(tick);
      };
      tick();
      toast.success(`¡Subiste de nivel! ${current.emoji} ${current.label}`, {
        description: "Sigue interactuando para desbloquear más recompensas.",
      });
      void trackEvent("level_up", { level: current.key, points });
    }
    lastLevel.current = current;
  }, [points, loading]);

  const award = useCallback(
    async (reason: keyof typeof POINT_RULES | string, metadata: Record<string, unknown> = {}) => {
      const a = await awardPoints(reason, metadata);
      if (a > 0) {
        await refresh();
        void trackEvent("points_awarded", { reason, amount: a });
      }
      return a;
    },
    [refresh],
  );

  return { points, loading, refresh, award, ...levelFor(points) };
}